import React, { useState, forwardRef, useCallback } from 'react'
import {
  FieldLabel,
  FieldValue,
  TextInput,
  ErrorMessage,
} from '@lightspeed/design-system-react'

interface FormInputProps {
  label: string
  supplementaryLabel?: string
  placeholder?: string
  id: string
  value: string
  errorMessage?: string
  onChange: (value: string) => void
  validation?: (value: string) => string
}

export const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  (props, ref) => {
    const {
      label,
      supplementaryLabel,
      placeholder,
      id,
      value,
      onChange,
      validation,
      errorMessage,
    } = props

    const [touched, setTouched] = useState(false)
    const [internalError, setInternalError] = useState<string | null>(null)

    const handleBlur = useCallback(() => {
      setTouched(true)
      const error = validation?.(value) || ''
      setInternalError(error)
    }, [validation, value])

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value
        onChange(newValue)
        if (touched) {
          setInternalError(validation?.(newValue) || '')
        }
      },
      [onChange, validation, touched]
    )

    const showError = (touched && internalError) || errorMessage

    return (
      <>
        <FieldLabel
          label={label}
          htmlFor={id}
          supplementaryLabel={supplementaryLabel}
        />
        <FieldValue>
          <TextInput
            id={id}
            ref={ref}
            value={value}
            placeholder={placeholder}
            onChange={handleChange}
            onBlur={handleBlur}
            aria-invalid={!!showError}
            hasError={!!showError}
          />
        </FieldValue>
        {showError && <ErrorMessage>{showError}</ErrorMessage>}
      </>
    )
  }
)

FormInput.displayName = 'FormInput'
